import {
  useCallback, useEffect, useReducer, useRef,
} from 'react';
import GameSocket from '../../brawl/net/GameSocket';
import { haptic, hapticNotify } from '../../brawl/utils/haptics';

/**
 * Durak over the shared game WebSocket (`durak.*` messages, see DurakHub on the server).
 * screen: lobby → searching → game → result (or error); «play with a friend»: lobby → room (invite link) → game,
 * the friend: lobby → searching (joining) → game. The server is authoritative: we only send
 * play / beat / take / done / leave and render whatever `durak.state` says.
 *
 * `table` here is the "live" table: `durak.event`s update it immediately (so animations start without waiting
 * for the state), `durak.state` then replaces it with the authoritative one. `fx` holds the cards that just
 * left the table (бито / pickup) for the exit animation.
 */

const RESULT_DELAY_MS = 1100;

const initial = {
  screen: 'lobby', // lobby | searching | room | game | result | error
  connection: 'idle',
  queue: null,
  room: null, // host's invite {code, stake, expiresInSec}
  joining: false, // searching = joining a friend's room
  match: null,
  state: null,
  table: [],
  fx: null,
  lastEvent: null,
  end: null,
  error: null,
};

function applyEvent(table, ev) {
  if (ev.e === 'play') return [...table, { a: ev.card, d: null }];
  if (ev.e === 'beat') return table.map((p) => (p.a === ev.target ? { ...p, d: ev.card } : p));
  if (ev.e === 'bito' || ev.e === 'pickup') return [];
  return table;
}

let fxSeq = 0;

export function durakReducer(s, a) {
  switch (a.type) {
    case 'connecting':
      return {
        ...s, screen: 'searching', queue: a.queue || null, joining: !!a.joining, error: null, end: null,
      };
    case 'roomOpening':
      return {
        ...s, screen: 'room', room: null, error: null, end: null,
      };
    case 'room':
      if (s.screen === 'game') return s;
      return { ...s, screen: 'room', room: a.msg };
    case 'connection':
      return { ...s, connection: a.status };
    case 'queue':
      if (s.screen === 'game') return s;
      return { ...s, screen: 'searching', queue: a.msg };
    case 'match': {
      const same = s.match && s.match.id === a.msg.id;
      return {
        ...s,
        screen: 'game',
        room: null,
        joining: false,
        match: a.msg,
        state: same ? s.state : null,
        table: same ? s.table : [],
        fx: same ? s.fx : null,
        end: null,
        error: null,
      };
    }
    case 'state': {
      if (s.state && s.match && s.state.v > a.msg.v && s.state.you === a.msg.you) return s; // stale
      return { ...s, state: { ...a.msg, receivedAt: a.at }, table: a.msg.table || [] };
    }
    case 'event': {
      const ev = a.msg;
      let { fx } = s;
      if (ev.e === 'bito' || ev.e === 'pickup') {
        fxSeq += 1;
        fx = {
          key: fxSeq, kind: ev.e, by: ev.by, cards: s.table,
        };
      }
      return {
        ...s, table: applyEvent(s.table, ev), fx, lastEvent: { ...ev, at: a.at },
      };
    }
    case 'end':
      return { ...s, end: a.msg };
    case 'showResult':
      return s.end ? { ...s, screen: 'result' } : s;
    case 'error':
      return { ...s, error: { ...a.error, at: a.at } };
    case 'clearError':
      return { ...s, error: null };
    case 'toLobby':
      return {
        ...initial, connection: s.connection, error: a.error ? { ...a.error, at: a.at } : null,
      };
    case 'failed':
      return { ...s, screen: s.screen === 'result' ? 'result' : 'error', error: { code: 'CONNECTION', msg: 'Connection lost' } };
    default:
      return s;
  }
}

export default function useDurakSession({ createSocket } = {}) {
  const [s, dispatch] = useReducer(durakReducer, initial);
  const sockRef = useRef(null);
  const pendingRef = useRef(null);
  const screenRef = useRef(s.screen);
  const resultTimer = useRef(null);
  screenRef.current = s.screen;

  const send = useCallback((msg) => {
    const sock = sockRef.current;
    if (!sock) return false;
    return sock.send(msg);
  }, []);

  const ensureSocket = useCallback(() => {
    if (sockRef.current && sockRef.current.status !== 'closed' && sockRef.current.status !== 'failed') {
      return sockRef.current;
    }
    const sock = createSocket ? createSocket() : new GameSocket({ maxReconnects: 3 });
    sockRef.current = sock;
    sock.on('status', (st) => dispatch({ type: 'connection', status: st }));
    sock.on('open', () => {
      if (pendingRef.current) {
        sock.send(pendingRef.current);
        sock.resumeMsg = pendingRef.current;
        pendingRef.current = null;
      }
    });
    sock.on('durak.queue.status', (msg) => dispatch({ type: 'queue', msg }));
    sock.on('durak.room.created', (msg) => {
      // after a reconnect the host gets the same room (code) back
      sock.resumeMsg = { t: 'durak.room.create', stake: msg.stake, code: msg.code };
      dispatch({ type: 'room', msg });
    });
    sock.on('durak.match', (msg) => {
      sock.resumeMsg = { t: 'durak.sync' }; // after a reconnect the server re-sends match + state (or the end)
      if (screenRef.current !== 'game') hapticNotify('success');
      dispatch({ type: 'match', msg });
    });
    sock.on('durak.state', (msg) => dispatch({ type: 'state', msg, at: Date.now() }));
    sock.on('durak.event', (msg) => {
      if (msg.e === 'play' || msg.e === 'beat') haptic('light');
      else if (msg.e === 'pickup') haptic('medium');
      dispatch({ type: 'event', msg, at: Date.now() });
    });
    sock.on('durak.end', (msg) => {
      sock.resumeMsg = null;
      if (screenRef.current !== 'game' && screenRef.current !== 'result') return; // stale end from an old game
      const won = msg.winner !== null && msg.winner !== undefined && msg.winner === msg.you;
      if (msg.winner === null || msg.winner === undefined) hapticNotify('warning');
      else hapticNotify(won ? 'success' : 'error');
      dispatch({ type: 'end', msg });
      clearTimeout(resultTimer.current);
      resultTimer.current = setTimeout(() => dispatch({ type: 'showResult' }), RESULT_DELAY_MS);
    });
    sock.on('error', (msg) => {
      const error = { code: msg.code, msg: msg.msg || msg.code };
      if (screenRef.current === 'searching' || screenRef.current === 'room') {
        sock.resumeMsg = null;
        dispatch({ type: 'toLobby', error, at: Date.now() });
        return;
      }
      dispatch({ type: 'error', error, at: Date.now() });
    });
    sock.on('failed', () => dispatch({ type: 'failed' }));
    return sock;
  }, [createSocket]);

  const findGame = useCallback((stake) => {
    const msg = { t: 'durak.queue.join', stake: String(stake ?? '0') };
    dispatch({ type: 'connecting', queue: { stake: String(stake ?? '0'), waitedSec: 0, botAfterSec: null } });
    const sock = ensureSocket();
    if (sock.isOpen && sock.isOpen()) {
      sock.send(msg);
      sock.resumeMsg = msg;
    } else {
      pendingRef.current = msg;
      if (sock.status !== 'connecting' && sock.status !== 'reconnecting') sock.connect();
    }
  }, [ensureSocket]);

  const sendOrConnect = useCallback((msg) => {
    const sock = ensureSocket();
    if (sock.isOpen && sock.isOpen()) {
      sock.send(msg);
      sock.resumeMsg = msg;
    } else {
      pendingRef.current = msg;
      if (sock.status !== 'connecting' && sock.status !== 'reconnecting') sock.connect();
    }
  }, [ensureSocket]);

  /** «Play with a friend»: open an invite room (→ `durak.room.created {code}`). */
  const createRoom = useCallback((stake) => {
    dispatch({ type: 'roomOpening' });
    sendOrConnect({ t: 'durak.room.create', stake: String(stake ?? '0') });
  }, [sendOrConnect]);

  const joinRoom = useCallback((code) => {
    dispatch({ type: 'connecting', queue: null, joining: true });
    sendOrConnect({ t: 'durak.room.join', code });
  }, [sendOrConnect]);

  /** A game is already running on the server (lobby probe): connect and ask for match + state. */
  const resume = useCallback(() => {
    const msg = { t: 'durak.sync' };
    dispatch({ type: 'connecting', queue: null });
    const sock = ensureSocket();
    if (sock.isOpen && sock.isOpen()) {
      sock.send(msg);
      sock.resumeMsg = msg;
    } else {
      pendingRef.current = msg;
      if (sock.status !== 'connecting' && sock.status !== 'reconnecting') sock.connect();
    }
  }, [ensureSocket]);

  const closeSocket = useCallback(() => {
    pendingRef.current = null;
    const sock = sockRef.current;
    sockRef.current = null;
    if (sock) {
      sock.resumeMsg = null;
      sock.close();
    }
  }, []);

  const cancelSearch = useCallback(() => {
    send({ t: 'durak.queue.leave' });
    closeSocket();
    dispatch({ type: 'toLobby' });
  }, [send, closeSocket]);

  const cancelRoom = useCallback(() => {
    send({ t: 'durak.room.cancel' });
    closeSocket();
    dispatch({ type: 'toLobby' });
  }, [send, closeSocket]);

  const surrender = useCallback(() => {
    send({ t: 'durak.leave' });
  }, [send]);

  const backToLobby = useCallback(() => {
    closeSocket();
    dispatch({ type: 'toLobby' });
  }, [closeSocket]);

  const clearError = useCallback(() => dispatch({ type: 'clearError' }), []);

  useEffect(() => () => {
    clearTimeout(resultTimer.current);
    const sock = sockRef.current;
    sockRef.current = null;
    if (sock) {
      try {
        sock.send({ t: 'durak.queue.leave' });
        if (screenRef.current === 'room') sock.send({ t: 'durak.room.cancel' });
      } catch (e) { /* ignore */ }
      sock.close();
    }
  }, []);

  return {
    ...s, send, findGame, resume, cancelSearch, createRoom, joinRoom, cancelRoom, surrender, backToLobby, clearError,
  };
}
